import { Component, Input } from '@angular/core';

/**
 * Shimmer placeholder for content that hasn't loaded yet (or doesn't exist).
 *
 * Variants:
 *   text-sm / text-md / text-lg → single line at the matching scale
 *   block-h-{N} → block of fixed height N px
 *   pill / badge → inline pill-shaped placeholders
 *   bar / bar-track → progress-bar style (track + filled portion)
 *   tile → 80px tall card-sized rect
 *
 * Width can be overridden per-instance via inline style.
 */
@Component({
    standalone: false,
    selector: 'mkt-ws-skel',
    template: `<span class="sk" [class]="'sk--' + variant" [style.width]="width"></span>`,
    styles: [`
        :host { display: inline-block; line-height: 1; }
        .sk { display: inline-block; background: linear-gradient(90deg, var(--mj-bg-page, #f5f5f5) 0%, color-mix(in srgb, var(--mj-text-disabled, #d4d4d0) 30%, var(--mj-bg-page, #f5f5f5)) 50%, var(--mj-bg-page, #f5f5f5) 100%); background-size: 200% 100%; animation: sk-shimmer 1.4s ease-in-out infinite; border-radius: 4px; vertical-align: middle; }
        .sk--text-sm { height: 12px; width: 100%; }
        .sk--text-md { height: 14px; width: 100%; }
        .sk--text-lg { height: 18px; width: 100%; }
        .sk--text-xl { height: 26px; width: 100%; border-radius: 4px; }
        .sk--pill { height: 18px; width: 60px; border-radius: 99px; }
        .sk--badge { height: 14px; width: 48px; border-radius: 99px; }
        .sk--bar-track { height: 6px; width: 100%; border-radius: 99px; }
        .sk--tile { height: 80px; width: 100%; border-radius: 8px; }
        .sk--block { height: 120px; width: 100%; border-radius: 8px; }
        .sk--card { height: 200px; width: 100%; border-radius: 10px; }
        .sk--avatar { height: 28px; width: 28px; border-radius: 50%; }
        .sk--icon { height: 20px; width: 20px; border-radius: 4px; }
        @keyframes sk-shimmer { 0% { background-position: 200% 0; } 100% { background-position: -200% 0; } }
    `]
})
export class SkeletonComponent {
    @Input() variant: SkeletonVariant = 'text-md';
    @Input() width = '';
}

export type SkeletonVariant =
    | 'text-sm'
    | 'text-md'
    | 'text-lg'
    | 'text-xl'
    | 'pill'
    | 'badge'
    | 'bar-track'
    | 'tile'
    | 'block'
    | 'card'
    | 'avatar'
    | 'icon';
